<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[75] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="提醒设置">
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="close"></div>
      <section class="relative flex max-h-[86vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header class="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 pb-4 pt-5">
          <div class="min-w-0">
            <h2 class="text-base font-semibold text-slate-900">提醒设置</h2>
            <p class="mt-1 text-sm leading-6 text-slate-500">设置你自己的计划与总结提醒时间。</p>
          </div>
          <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭提醒设置" aria-label="关闭提醒设置" :disabled="saving" @click="close">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </header>

        <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <div v-if="loading" class="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-10 text-center">
            <div class="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
            <span class="text-sm text-slate-500">正在加载提醒设置...</span>
          </div>
          <form v-else class="space-y-4" @submit.prevent="submit">
            <section class="rounded-lg border border-slate-200 bg-white px-4 py-3">
              <label class="form-switch">
                <input v-model="form.planReminderEnabled" class="form-switch-input" type="checkbox">
                <span class="form-switch-track"></span>
                <span class="form-switch-label">计划提交提醒</span>
              </label>
              <label class="mt-3 block">
                <span class="form-label">相对计划截止（分钟）</span>
                <input v-model.number="form.planReminderOffsetMinutes" class="form-input mt-1 w-full" type="number" :disabled="!form.planReminderEnabled">
              </label>
              <p class="mt-1 text-xs text-slate-500">{{ reminderOffsetLabel(form.planReminderEnabled, form.planReminderOffsetMinutes) }}</p>
            </section>

            <section class="rounded-lg border border-slate-200 bg-white px-4 py-3">
              <label class="form-switch">
                <input v-model="form.summaryReminderEnabled" class="form-switch-input" type="checkbox">
                <span class="form-switch-track"></span>
                <span class="form-switch-label">总结提交提醒</span>
              </label>
              <label class="mt-3 block">
                <span class="form-label">相对总结截止（分钟）</span>
                <input v-model.number="form.summaryReminderOffsetMinutes" class="form-input mt-1 w-full" type="number" :disabled="!form.summaryReminderEnabled">
              </label>
              <p class="mt-1 text-xs text-slate-500">{{ reminderOffsetLabel(form.summaryReminderEnabled, form.summaryReminderOffsetMinutes) }}</p>
            </section>

            <p class="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-700">
              负数表示截止前提醒，0 表示截止时提醒，正数表示截止后提醒。
            </p>
          </form>
        </div>

        <footer class="flex shrink-0 justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
          <button type="button" class="btn-secondary" :disabled="saving" @click="close">取消</button>
          <button type="button" class="btn-primary" :disabled="loading || saving" @click="submit">
            {{ saving ? '保存中' : '保存设置' }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  setting: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['update:modelValue', 'submit'])

const form = reactive({
  planReminderEnabled: false,
  planReminderOffsetMinutes: -30,
  summaryReminderEnabled: false,
  summaryReminderOffsetMinutes: -20
})

watch(
  () => props.setting,
  (setting) => syncForm(setting),
  { immediate: true, deep: true }
)

function syncForm(setting = {}) {
  form.planReminderEnabled = setting?.planReminderEnabled === true
  form.planReminderOffsetMinutes = integerOrDefault(setting?.planReminderOffsetMinutes, -30)
  form.summaryReminderEnabled = setting?.summaryReminderEnabled === true
  form.summaryReminderOffsetMinutes = integerOrDefault(setting?.summaryReminderOffsetMinutes, -20)
}

function close() {
  if (!props.saving) emit('update:modelValue', false)
}

function submit() {
  if (props.loading || props.saving) return
  if (form.planReminderEnabled && !Number.isInteger(Number(form.planReminderOffsetMinutes))) {
    ElMessage.warning('请填写有效的计划提醒时间')
    return
  }
  if (form.summaryReminderEnabled && !Number.isInteger(Number(form.summaryReminderOffsetMinutes))) {
    ElMessage.warning('请填写有效的总结提醒时间')
    return
  }
  emit('submit', {
    planReminderEnabled: form.planReminderEnabled,
    planReminderOffsetMinutes: form.planReminderEnabled ? Number(form.planReminderOffsetMinutes) : null,
    summaryReminderEnabled: form.summaryReminderEnabled,
    summaryReminderOffsetMinutes: form.summaryReminderEnabled ? Number(form.summaryReminderOffsetMinutes) : null
  })
}

function reminderOffsetLabel(enabled, offset) {
  if (!enabled) return '未开启'
  const value = Number(offset)
  if (!Number.isFinite(value)) return '已开启'
  if (value === 0) return '截止时'
  return value < 0 ? `截止前 ${Math.abs(value)} 分钟` : `截止后 ${value} 分钟`
}

function integerOrDefault(value, fallback) {
  if (value === null || value === undefined || value === '') return fallback
  const number = Number(value)
  return Number.isInteger(number) ? number : fallback
}
</script>
